(() => {
  if (window.__hsInit) return;
  window.__hsInit = true;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function initReveal(root) {
    const els = root.querySelectorAll('[data-hs-reveal]:not(.is-in)');
    if (reduce || !('IntersectionObserver' in window)) {
      els.forEach((el) => el.classList.add('is-in'));
      return;
    }
    const io = new IntersectionObserver((entries) => {
      entries.forEach((e) => {
        if (e.isIntersecting) {
          e.target.classList.add('is-in');
          io.unobserve(e.target);
        }
      });
    }, { rootMargin: '0px 0px -10% 0px' });
    els.forEach((el) => io.observe(el));
  }

  function initSnow(canvas) {
    if (reduce || !canvas || canvas.dataset.ready) return;
    canvas.dataset.ready = '1';
    const ctx = canvas.getContext('2d');
    let w, h, flakes = [], running = true;
    const dpr = Math.min(window.devicePixelRatio || 1, 2);
    function resize() {
      w = canvas.clientWidth; h = canvas.clientHeight;
      canvas.width = w * dpr; canvas.height = h * dpr;
      ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
      const count = Math.round(Math.min(160, (w * h) / 9000));
      flakes = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        r: Math.random() * 1.8 + 0.3, s: Math.random() * 0.6 + 0.25,
        d: Math.random() * 0.6 - 0.3, o: Math.random() * 0.6 + 0.2
      }));
    }
    function tick() {
      if (!running) return;
      ctx.clearRect(0, 0, w, h);
      for (const f of flakes) {
        f.y += f.s; f.x += f.d + Math.sin(f.y / 60) * 0.2;
        if (f.y > h) { f.y = -4; f.x = Math.random() * w; }
        ctx.beginPath();
        ctx.fillStyle = `rgba(225,235,245,${f.o})`;
        ctx.arc(f.x, f.y, f.r, 0, Math.PI * 2);
        ctx.fill();
      }
      requestAnimationFrame(tick);
    }
    resize();
    window.addEventListener('resize', resize);
    new IntersectionObserver(([e]) => {
      const was = running; running = e.isIntersecting;
      if (running && !was) requestAnimationFrame(tick);
    }).observe(canvas);
    requestAnimationFrame(tick);
  }

  function initParallax() {
    if (reduce) return;
    const items = () => document.querySelectorAll('[data-hs-parallax]');
    let ticking = false;
    function update() {
      ticking = false;
      const vh = window.innerHeight;
      items().forEach((el) => {
        const rect = el.getBoundingClientRect();
        if (rect.bottom < -200 || rect.top > vh + 200) return;
        const speed = parseFloat(el.dataset.hsParallax) || 0.15;
        const center = rect.top + rect.height / 2 - vh / 2;
        const v = (-center * speed).toFixed(1);
        if (el.dataset.hsVar) el.style.setProperty(el.dataset.hsVar, v + 'px');
        else el.style.transform = `translate3d(0, ${v}px, 0)`;
      });
    }
    window.addEventListener('scroll', () => {
      if (!ticking) { ticking = true; requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  function initCounters(root) {
    root.querySelectorAll('[data-hs-count]').forEach((el) => {
      if (el.dataset.done) return;
      const target = parseFloat(el.dataset.hsCount);
      if (isNaN(target) || reduce) { el.dataset.done = 1; return; }
      const io = new IntersectionObserver(([e]) => {
        if (!e.isIntersecting) return;
        io.disconnect(); el.dataset.done = 1;
        const start = performance.now(), dur = 1400;
        const step = (t) => {
          const p = Math.min(1, (t - start) / dur);
          const eased = 1 - Math.pow(1 - p, 3);
          el.textContent = Math.round(target * eased);
          if (p < 1) requestAnimationFrame(step);
        };
        el.textContent = '0';
        requestAnimationFrame(step);
      }, { threshold: 0.6 });
      io.observe(el);
    });
  }

  function initGear(root) {
    root.querySelectorAll('[data-hs-gear]').forEach((gear) => {
      if (gear.dataset.ready) return;
      gear.dataset.ready = '1';
      const steps = [...gear.querySelectorAll('.hs-step')];
      const frames = [...gear.querySelectorAll('.hs-gear__frame')];
      const cur = gear.querySelector('[data-hs-current]');
      const label = gear.querySelector('[data-hs-label]');
      const bar = gear.querySelector('[data-hs-bar]');
      const total = steps.length;
      function activate(i) {
        steps.forEach((s, j) => s.classList.toggle('is-active', j === i));
        frames.forEach((f, j) => f.classList.toggle('is-active', j === i));
        if (cur) cur.textContent = String(i + 1).padStart(2, '0');
        if (label) label.textContent = steps[i]?.dataset.label || '';
        if (bar) bar.style.width = ((i + 1) / total) * 100 + '%';
      }
      activate(0);
      if (!('IntersectionObserver' in window)) return;
      const io = new IntersectionObserver((entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) activate(steps.indexOf(e.target));
        });
      }, { rootMargin: '-45% 0px -45% 0px' });
      steps.forEach((s) => io.observe(s));
    });
  }

  function initBuybar() {
    const bar = document.querySelector('[data-hs-buybar]');
    const hero = document.querySelector('.hs-hero');
    if (!bar || !hero || bar.dataset.ready) return;
    bar.dataset.ready = '1';
    if (!(window.Shopify && Shopify.designMode)) document.body.appendChild(bar);
    new IntersectionObserver(([e]) => bar.classList.toggle('is-visible', !e.isIntersecting)).observe(hero);
  }

  function init(root = document) {
    initReveal(root);
    root.querySelectorAll('.hs-hero__snow').forEach(initSnow);
    initCounters(root);
    initGear(root);
    initBuybar();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => { init(); initParallax(); });
  else { init(); initParallax(); }
  document.addEventListener('shopify:section:load', (e) => init(e.target));
})();
